import React from 'react';

import Icon from '../Icon';
import OutlinedInput from '../OutlinedInput';
import IconButton from '../IconButton';
import Typography from '../Typography';

import { useMUITheme } from '../styles/theme';
import { css } from '../../customEmotion';

type PaginationProps = {
  /**
   * Current pageIndex.
   */
  pageIndex: number;
  /**
   * Total pages.
   */
  totalPages: number;
  setPageIndex: (pageIndex: number) => void;
};

const KEYCODE = {
  ENTER: 13,
};

const Pagination: React.FC<PaginationProps> = ({
  pageIndex,
  totalPages,
  setPageIndex,
}) => {
  const theme = useMUITheme();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [innerPageIndex, setInnerPageIndex] = React.useState<string>('1');

  React.useEffect(() => {
    setInnerPageIndex((pageIndex + 1).toString());
  }, [pageIndex]);

  React.useEffect(() => {
    if (totalPages <= 0) {
      setPageIndex(-1);
    } else if (totalPages - 1 < pageIndex) {
      setPageIndex(0);
    }
  }, [totalPages]);

  const prev = React.useCallback(() => {
    if (pageIndex > 0) {
      setInnerPageIndex(pageIndex.toString());
      setPageIndex(pageIndex - 1);
    }
  }, [pageIndex, setPageIndex, setInnerPageIndex]);

  const next = React.useCallback(() => {
    if (pageIndex < totalPages - 1) {
      setInnerPageIndex((pageIndex + 2).toString());
      setPageIndex(pageIndex + 1);
    }
  }, [pageIndex, totalPages, setPageIndex, setInnerPageIndex]);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    event => {
      const isNumber = event.target.value.match('^[0-9]*$');
      const pageIndexFromEvent = parseInt(event.target.value, 10);

      if (
        pageIndexFromEvent >= 1 &&
        pageIndexFromEvent <= totalPages &&
        isNumber &&
        Number.isInteger(pageIndexFromEvent) &&
        totalPages > 0
      ) {
        setInnerPageIndex(event.target.value);
      } else if (!pageIndexFromEvent) {
        setInnerPageIndex('');
      }
    },
    [totalPages, setInnerPageIndex]
  );

  const onBlur = React.useCallback(() => {
    const isNumber = innerPageIndex.match('^[0-9]*$');

    if (innerPageIndex && isNumber) {
      setPageIndex(parseInt(innerPageIndex, 10) - 1);
    } else {
      setInnerPageIndex((pageIndex + 1).toString());
    }
  }, [innerPageIndex, setPageIndex, setInnerPageIndex]);

  const onKeyUp: React.KeyboardEventHandler = React.useCallback(
    event => {
      if (event.keyCode === KEYCODE.ENTER) {
        onBlur();
        inputRef.current && inputRef.current.blur();
      }
    },
    [onBlur, inputRef]
  );

  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex < totalPages - 1;

  return (
    <div
      className={css`
        display: inline-flex;
        align-items: center;
        color: ${theme.palette.grey[500]};
      `}
    >
      <div
        className={css`
          padding: 0 8px;
        `}
      >
        <IconButton
          disabled={!hasPrev}
          onClick={prev}
          tabIndex={hasPrev ? 0 : undefined}
          aria-disabled={!hasPrev}
          size="small"
        >
          <Icon name="NavigateBefore" />
        </IconButton>
      </div>
      <div
        className={css`
          display: flex;
          align-items: center;
        `}
        data-page={pageIndex + 1}
        data-total-pages={totalPages}
      >
        <div
          className={css`
            max-width: 60px;
          `}
        >
          <OutlinedInput
            inputRef={inputRef}
            type="text"
            value={innerPageIndex}
            onChange={onChange}
            onKeyUp={onKeyUp}
            onBlur={onBlur}
            inputProps={{
              style: {
                textAlign: 'center',
              },
            }}
          />
        </div>

        <div
          className={css`
            padding: 0 16px;
          `}
        >
          {' '}
          <Typography variant="body1">&#47;</Typography>
        </div>
        <Typography variant="body1">{totalPages}</Typography>
      </div>
      <div
        className={css`
          padding: 0 8px;
        `}
      >
        <IconButton
          name="NavigateNext"
          disabled={!hasNext}
          onClick={next}
          tabIndex={hasNext ? 0 : undefined}
          aria-disabled={!hasNext}
          size="small"
        >
          <Icon name="NavigateNext" />
        </IconButton>
      </div>
    </div>
  );
};

Pagination.displayName = 'Pagination';

export default Pagination;
